import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { SettingOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import { getMeta, getMissing, saveSettings } from "./api";
import ProjectDiary from "./ProjectDiary";
import { LIST, dayUrl } from "./format";
import "./siteDiary.css";

/** Company settings: when a day locks, and the missing-entry reminders. */
const SettingsDialog = ({ open, settings, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) form.setFieldsValue(settings);
  }, [open]);

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const res = await saveSettings(values);
      Success(t("Saved"));
      onSaved(res.data.settings);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      rootClassName="site-diary-page"
      open={open}
      onCancel={onClose}
      title={t("Site diary settings")}
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={save}>
          {t("Save")}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="lockAfterHours"
          label={t("Lock entries after (hours after the end of the day)")}
          extra={t(
            "A locked entry is evidence: later changes are only possible as a correction with a reason. 0 = only when signed."
          )}
          rules={[{ required: true }]}
        >
          <InputNumber min={0} max={720} precision={0} className="sdi-full" />
        </Form.Item>
        <Form.Item
          name="reminders"
          label={t("Remind about missing entries")}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item name="reminderMode" label={t("A day needs an entry when")}>
          <Segmented
            block
            options={[
              { value: "workdays", label: t("Every working day") },
              { value: "attendance", label: t("Time was booked on the project") }
            ]}
          />
        </Form.Item>
        <Form.Item name="reminderDays" label={t("Look back (days)")}>
          <InputNumber min={1} max={30} precision={0} className="sdi-full" />
        </Form.Item>
        <Typography.Paragraph type="secondary" className="sdi-small">
          {t(
            "Every morning the project leader (or the company without one) gets an email and a task for running projects with missing days."
          )}
        </Typography.Paragraph>
      </Form>
    </Modal>
  );
};

/** Missing days of the running projects, each one a link to that day. */
const MissingCard = ({ missing, onOpen }) => {
  const { t } = useTranslation("dashboard");
  if (!missing?.projects?.length) return null;
  return (
    <Alert
      type="warning"
      showIcon
      icon={<WarningOutlined />}
      className="sdi-missing"
      message={t("Missing diary entries")}
      description={
        <Space direction="vertical" size={6} className="sdi-full">
          {missing.projects.map(item => (
            <div key={item.project}>
              <Typography.Text strong>{item.projectName}</Typography.Text>{" "}
              {item.days.map(day => (
                <Tag
                  key={day}
                  color="error"
                  className="sdi-day-tag"
                  onClick={() => onOpen(item.project, day)}
                >
                  {dayjs(day).format("dd DD.MM.")}
                </Tag>
              ))}
            </div>
          ))}
        </Space>
      }
    />
  );
};

const SiteDiary = () => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const location = useLocation();
  const [meta, setMeta] = useState(null);
  const [missing, setMissing] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const project = new URLSearchParams(location.search).get("project") || "";

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
    getMissing()
      .then(res => setMissing(res.data))
      .catch(generalErrorAlert);
  }, []);

  const chooseProject = id => history.replace(id ? `${LIST}?project=${id}` : LIST);

  if (!meta) {
    return (
      <div className="site-diary-page">
        <Skeleton active />
      </div>
    );
  }

  // Running projects first, then the rest by creation (newest first)
  const projects = [...meta.projects].sort((a, b) => Number(b.active) - Number(a.active));
  const current = projects.find(p => String(p._id) === project);

  return (
    <div className="site-diary-page">
      <PageToolbar
        filters={
          <Select
            showSearch
            allowClear
            className="sdi-project-select"
            placeholder={t("Choose a project")}
            value={project || undefined}
            onChange={value => chooseProject(value || "")}
            optionFilterProp="label"
            options={[
              {
                label: t("Running projects"),
                options: projects
                  .filter(p => p.active)
                  .map(p => ({ value: String(p._id), label: p.name }))
              },
              {
                label: t("Other projects"),
                options: projects
                  .filter(p => !p.active)
                  .map(p => ({ value: String(p._id), label: p.name }))
              }
            ]}
          />
        }
        actions={
          meta.can.settings ? (
            <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
              {t("Settings")}
            </Button>
          ) : null
        }
      />

      <MissingCard missing={missing} onOpen={(p, day) => history.push(dayUrl(p, day))} />

      {current ? (
        <Card className="sdi-card" title={current.name}>
          <ProjectDiary key={current._id} project={String(current._id)} can={meta.can} />
        </Card>
      ) : (
        <Empty
          className="sdi-empty"
          description={t(
            "Choose a project to see its diary. Each project has one page per day, written together by everyone on site."
          )}
        />
      )}

      <SettingsDialog
        open={settingsOpen}
        settings={meta.settings}
        onClose={() => setSettingsOpen(false)}
        onSaved={settings => {
          setMeta(prev => ({ ...prev, settings }));
          setSettingsOpen(false);
          getMissing()
            .then(res => setMissing(res.data))
            .catch(generalErrorAlert);
        }}
      />
    </div>
  );
};

export default SiteDiary;
